import Ajv from 'ajv';
import JSZip from 'jszip'
import lasHeader from 'las-header'
import deepmerge from 'deepmerge'
import { FetchSensorEquipmentListResponse } from './UploaderTypes'

// wheels up/down should be within this margin(earlier or later) of the first/last FMS GPS time
const FMS_GPS_TIME_MARGIN_IN_MS = 120 * 60 * 1000

interface AlertMessage {
    name: string;           // category
    message: any;
}
export interface FileRuleSummary {
    anyFileName: { [key: string]: Array<string> };
    basePath: string;
    errorMessages: Array<string>;
    alertMessages: Array<AlertMessage>;
    flightParts: { [key: string]: any };
    invalidFiles: Array<string>;
    uploadTypeLabel: string;
    validFiles: Array<string>;
}

interface FolderValidationHelperProps {
    sensorEquipment: FetchSensorEquipmentListResponse;
    sensorName: string;
    primarySensorName: string;
    fileList: Array<File>;
    flightId: string;
    // sensorNumber: string;
    projectIdList: Array<string>;
    isPreexistingUpload: boolean;
    uploadTypes: Array<string>;
    maxChunkSizeMB: number;
    isSimultaneousImagery: boolean;
    processingTemplate: string;
    departureTimezone: string;
    wheelsUp: string;
    wheelsDown: string;
}

export interface FolderValidationHelperReturn {
    // isValid: boolean;
    fileRuleSummary: FileRuleSummary;
    isMissingFlightplanCS: boolean;
}

export interface FMSLogMismatchProjectAlertMessage {
    fmsLogFilenameListWithNoProjectId?: Array<string>;
    fmsLogFilenameListWithUnexpectedProjectId?: Array<string>;
    missingProjectIdList?: Array<string>;
}

export interface FMSLogMismatchTimeAlertMessage {
    fmsLogFilenameList: Array<string>;
}

// Function to parse date string to Date object
function parseT04DateString(dateStr: string): Date {
    const year: number = parseInt(dateStr.slice(0, 4), 10);
    // const month: number = parseInt(dateStr.slice(4, 6), 10) - 1; // Months are 0-based in JS
    const month: number = parseInt(dateStr.slice(4, 6), 10);
    const day: number = parseInt(dateStr.slice(6, 8), 10);
    const hour: number = parseInt(dateStr.slice(8, 10), 10);
    const minute: number = parseInt(dateStr.slice(10, 12), 10);
    const second: number = parseInt(dateStr.slice(12, 14), 10) || 0; // Ensure seconds are included
    // return new Date(year, month, day, hour, minute, second);

    const utcString: string = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}.000Z`;
    // console.log(`...${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`)
    // console.log("...utcString ",utcString);
    return new Date(utcString);
}

const readFileAsync = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsText(file);
    })
}

const readFileAsyncAsArrayBuffer = (file: Blob): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    })
}

// Check if subsetArray contains all elements of parentArray
const checkSubsetArrContain = (parentArray: Array<string>, subsetArray: Array<string>) => {
    if (parentArray.length == 0) {
        return true
    }
    if (subsetArray.length) {
        return subsetArray.every((el) => {
            return parentArray.includes(el)
        })
    } else {
        return false
    }
}

const convertFmsGpsTimeToUtc = (missionDateUtcString: string, gpsTimeString: string, leapSecondOffset: number) => {
    let missionDate = new Date(missionDateUtcString + 'T00:00:00+0000')
    let dayOfWeek = missionDate.getDay()
    let utcDate = new Date(missionDate.getTime() + (parseFloat(gpsTimeString) * 1000) - (dayOfWeek * 60 * 60 * 24 * 1000) + (leapSecondOffset * 1000))
    return utcDate;
}

const folderValidationHelper = async (props: FolderValidationHelperProps): Promise<FolderValidationHelperReturn> => {
    const {
        sensorEquipment, sensorName, flightId, projectIdList, isPreexistingUpload,
        maxChunkSizeMB, isSimultaneousImagery, processingTemplate, primarySensorName
    } = props
    var { uploadTypes, fileList } = props

    let hasFlightPlan = false;
    let hasMinOneCS = false;
    let flightPlanParseError = false;

    let basePath = (fileList[0] as any).webkitRelativePath.split("/")[0]; //not safe
    // let fileMetadataSummaryList = [];
    let fileRuleSummary: FileRuleSummary = {
        basePath: basePath,
        flightParts: {},
        anyFileName: {},
        invalidFiles: [],
        validFiles: [],
        errorMessages: [],
        alertMessages: [],
        uploadTypeLabel: '',
    };

    // console.log("..sensorName",sensorName)
    //if Imagery, parsing sensor name for imagery type
    if (['LargeFormatImagery', 'MediumFormatImagery'].includes(uploadTypes[0])) {
        if (RegExp(/ A3 /, "g").test(sensorName)) {
            uploadTypes = ["A3_IMAGERY"];
        }
        else if (RegExp(/ DMC /, "g").test(sensorName)) {
            uploadTypes = ["DMC_IMAGERY"];
        }
        else {
            uploadTypes = ["IMAGERY"];
        }
    }
    if (['LargeFormatImageryGPS', 'MediumFormatImageryGPS'].includes(uploadTypes[0])) {
        if (RegExp(/A3/, "g").test(sensorName)) {
            uploadTypes = ["A3_IMAGERY_GPS"];
        }
        else if (RegExp(/DMC/, "g").test(sensorName)) {
            uploadTypes = ["DMC_IMAGERY_GPS"];
        }
        else {
            uploadTypes = ["IMAGERY_GPS"];
        }
    }
    if (['OptechGPS'].includes(uploadTypes[0])) {
        uploadTypes = ["OPTECH_GPS"];
    }

    let ajv = new Ajv({ allErrors: true, verbose: true });

    ajv.addKeyword('contiguousNumericRange', {
        validate: function (schema: any, data: any) {
            data = data.filter((item: string) => RegExp(/FL\d{6}.\d{3}/, "g").test(item))
            if (data.length === 0)
                return true;
            let isContiguous = data.every(function (item: string, index: number, array: Array<any>) {
                if (index > 0) {
                    let currentIntValue = parseInt(item.slice(-3))
                    let prevIntValue = parseInt(array[index - 1].slice(-3));
                    return currentIntValue === prevIntValue + 1;
                }
                return true;
            });
            return isContiguous;
        },
        errors: true
    });

    let rulesSchema: { [key: string]: any } = {
        "fileTypes": {
            "pdf": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\\.(pdf|PDF)$", "errorMessage": "Netsuite flight log report file name  (*.pdf) violates the naming standard, or doesn't match the FlightID:", "category": "lidarAndImagery", "uploadOrder": 1
            },
            "kmz": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\\.(kmz|KMZ)$", "errorMessage": "LiDAR coverage overview file name  (*.kmz) violates the naming standard, or doesn't match the FlightID:", "category": "lidar", "uploadOrder": 2
            },
            // "kmz_flightplan": {
            //     "type": "string", "pattern": "^(?!.*\\/)PRJ.*\.(KMZ|kmz)$", "errorMessage": "LiDAR flight plan file name (*.kmz) violates the naming standard and these must be in the root folder:", "category": "lidar", "uploadOrder": 16
            // },
            // "kmz_flightplan": {
            //     "type": "string", "pattern": "^\\d{8}.*\.(KMZ|kmz)$", "errorMessage": "LiDAR flight plan file name (*.kmz) violates the naming standard and these must be in the root folder:", "category": "lidar", "uploadOrder": 16
            // },
            "kmz_flightplan": {
                "type": "string", "pattern": "^(\\d{8}.*)|(PRJ\\d{5}.*)\.(KMZ|kmz)$", "errorMessage": "LiDAR flight plan file name (*.kmz) violates the naming standard and these must be in the root folder:", "category": "lidar", "uploadOrder": 16
            },
            "csv": {
                "type": "string", "pattern": "^FLIGHT_ID(_IR)?(_\\d{2})?\\.(csv|CSV)$", "errorMessage": "EXIF log file name  (*.csv) violates the naming standard, or doesn't match the FlightID:", "category": "imagery", "uploadOrder": 3
            },
            "xls": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\\.(xls|XLS)$", "errorMessage": "Excel file name  (*.xls) violates the naming standard, or doesn't match the FlightID:", "category": "imagery", "uploadOrder": 16
            },
            // "gps": {
            //     "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\.(\\d{3}|\\d{2}n|\\d{2}g|\\d{1}n|\\d{1}g|n|g)$", "errorMessage": "GPS file name violates the naming standard, or doesn't match the FlightID:", "category": "lidarAndImagery", "uploadOrder": 3
            // },
            "gps": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})*\.(\\d{3}|\\d{2}n|\\d{2}g|\\d{1}n|\\d{1}g|n|g)$", "errorMessage": "GPS file name violates the naming standard, or doesn't match the FlightID:", "category": "lidarAndImagery", "uploadOrder": 4
            },
            // "gps": {
            //     "type": "string", "pattern": "^(.+\\.T04)|(FLIGHT_ID\\d{6}\\.\\d{3})$", "errorMessage": "GPS file name violates the naming standard, or doesn't match the FlightID:", "category": "lidarAndImagery", "uploadOrder": 4
            // },
            "pos": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})*\.(pos|POS)$", "errorMessage": "GPS file name violates the naming standard, or doesn't match the FlightID:", "category": "lidarAndImagery", "uploadOrder": 5
            },
            // "pos": {
            //     "type": "string", "pattern": "^FLIGHT_ID\\.(pos|POS)$", "errorMessage": "GPS file name violates the naming standard, or doesn't match the FlightID:", "category": "lidarAndImagery", "uploadOrder": 5
            // },
            "gpso": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\\.(gpso|GPSO)$", "errorMessage": "GNSS / INS file (*.GPSO) name violates the naming standard, or doesn't match the FlightID:", "category": "imagery", "uploadOrder": 6
            },
            "gpsn": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\\.(gpsn|GPSN)$", "errorMessage": "GNSS / INS file (*.GPSN) name violates the naming standard, or doesn't match the FlightID:", "category": "imagery", "uploadOrder": 7
            },
            "range": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\.range$", "errorMessage": "Range file name violates the naming standard, or doesn't match the FlightID:", "category": "lidar", "uploadOrder": 8
            },
            "jpg": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\\.(jpg|JPG)$", "errorMessage": "Imagery coverage overview file name (*.jpg) violates the naming standard, or doesn't match the FlightID:", "category": "imagery", "uploadOrder": 9
            },
            "jgw": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\\.(jgw|JGW)$", "errorMessage": "Imagery coverage overview file name (*.jgw) violates the naming standard, or doesn't match the FlightID:", "category": "imagery", "uploadOrder": 10
            },
            "iiq": {
                "type": "string", "pattern": "^(.*/)*(\\w|_|-|\\.)+\\.(IIQ|iiq)$", "errorMessage": "Imagery file name (*.iiq) violates the naming standard:", "category": "imagery", "uploadOrder": 11
            },
            "iiq_no_ir": {
                "type": "string", "pattern": "^(.*/)*C(?!0067)(\\w|_|-|\\.)+\\.(IIQ|iiq)$", "errorMessage": "Imagery file name (*.iiq) violates the naming standard:", "category": "imagery", "uploadOrder": 11
            },
            "iiq_w_ir": {
                "type": "string", "pattern": "^(.*/)*C(\\w|_|-|\\.)+\\.(IIQ|iiq)$", "errorMessage": "Imagery file name (*.iiq) violates the naming standard:", "category": "imagery", "uploadOrder": 11
            },
            "rawimage": {
                "type": "string", "pattern": "^FLIGHT_ID(_\\d{2})?\\.(rawimage|RAWIMAGE)$", "errorMessage": "Raw image file name (*.rawimage) violates the naming standard, or doesn't match the FlightID:", "category": "imagery", "uploadOrder": 12
            },
            "bin": {
                "type": "string", "pattern": "^(.*/)*(\\w|_|-|\\.)+\\.(bin|BIN)$", "errorMessage": "Imagery file (*.BIN) name violates the naming standard:", "category": "imagery", "uploadOrder": 13
            },
            "dat": {
                "type": "string", "pattern": "^.+\\.(dat|DAT)$", "errorMessage": "Imagery file (*.DAT) name violates the naming standard:", "category": "imagery", "uploadOrder": 14
            },
            "log": {
                "type": "string", "pattern": "^FMS_Flight_Log_.+\\.log$", "errorMessage": "LiDAR FMS Nav log file name (*.log) violates the naming standard and these must be in the root folder:", "category": "lidar", "uploadOrder": 17
            },
            "dmcGpsDat": {
                "type": "string", "pattern": ".+_GnssImu\\.(dat|DAT|)$", "errorMessage": "Terrain Mapper GPS file name (*.dat) violates the naming standard:",
            },
            // "laz": {
            //     "type": "string", "pattern": "^\\d{3}_\\d{6}_\\d{4}-\\d{5}\\.(laz|LAZ)$", "errorMessage": "Terrain Mapper LiDAR file name  (*.laz) violates the naming standard, or doesn't match the FlightID:", 
            // },
            "laz": {
                "type": "string", "pattern": "\\.(laz|LAZ)$", "errorMessage": "Terrain Mapper LiDAR file name (*.laz) violates the naming standard:",
            },
            "sol": {
                "type": "string", "pattern": "\\.(sol|SOL)$", "errorMessage": "Terrain Mapper SOL file name (*.sol) violates the naming standard:",
            },
            "tmGpsDat": {
                "type": "string", "pattern": ".*MM1.+_GnssImu\\.(dat|DAT|dat\\.sha1)$", "errorMessage": "Terrain Mapper GPS file name (*.dat) violates the naming standard:",
            },
            "tmGpsRnv": {
                "type": "string", "pattern": ".*MM1.+_GnssImuRts\\.(rnv|RNV|rnv\\.sha1)$", "errorMessage": "Terrain Mapper GPS file name (*.rnv) violates the naming standard:",
            },
            "tmGpsTimeSync": {
                "type": "string", "pattern": ".*MM1.+TimeSync\\.(raw||raw\\.sha1)$", "errorMessage": "Terrain Mapper TimeSync.raw file violates the naming standard:",
            },
            "tmRaw": {
                "type": "string", "pattern": ".(raw|RAW)$", "errorMessage": "Terrain Mapper Lidar raw (*.raw) violates the naming standard:",
            },
            "tmRawSha1": {
                "type": "string", "pattern": ".(raw.sha1|RAW.SHA1)$", "errorMessage": "Terrain Mapper Lidar raw sha1 (*.raw.sha1) violates the naming standard:",
            },
            "tmDat": {
                "type": "string", "pattern": ".(dat|DAT)$", "errorMessage": "Terrain Mapper raw Lidar dat (*.dat) violates the naming standard:",
            },
            "tmDatSha1": {
                "type": "string", "pattern": ".(dat.sha1|DAT.SHA1)$", "errorMessage": "Terrain Mapper raw Lidar dat sha1 (*.dat.sha1) violates the naming standard:",
            },
            "tmThumbnails": {
                "type": "string", "pattern": "Thumbnails/", "errorMessage": "Terrain Mapper Imagery files (/Thumbnails/...)violates the naming standard:",
            },
            "tmDarkFrames": {
                "type": "string", "pattern": "DarkFrames/", "errorMessage": "Terrain Mapper Imagery files (/DarkFrames/...) violates the naming standard:",
            },
            "tmImageryReturnsDat": {
                "type": "string", "pattern": "MM\\d{1,2}\/[\\w\\d\\s\-_\/]+_Returns.(dat|DAT)$", "errorMessage": "Terrain Mapper raw imagery return dat (*_Returns.dat) violates the naming standard:",
            },
            "tmImageryReturnsDatSha1": {
                "type": "string", "pattern": "MM\\d{1,2}\/[\\w\\d\\s\-_\/]+_Returns.(dat.sha1|DAT.SHA1)$", "errorMessage": "Terrain Mapper raw imagery return dat sha1 (*_Returns.dat.sha1) violates the naming standard:",
            },
            "allFilesImageryTarget": {
                "type": "string", "pattern": "^.+$", "errorMessage": "", "category": "imagery", "uploadOrder": 15
            },
            "allFilesTerrainMapperRAW": {
                "type": "string", "pattern": "^.+$", "errorMessage": "", "category": "terrain_mapper"
            },
            // "edgePOS": {
            //     "type": "string", "pattern": "^.+(pos|POS)$", "errorMessage": "edge pos"
            // },
            // "edgeT04": {
            //     "type": "string", "pattern": "^.+(t04|T04)$", "errorMessage": "edge t04"
            // },
            "africaGps": {
                "type": "string", "pattern": "^.+\\.T04$", "errorMessage": "blah"
            },
        },
        "LiDAR": {
            "labels": ["LiDAR"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "flightParts": {
                    "type": "array",
                    "minItems": 1,
                    "errorMessage": "A minimum of at least one set of Flight GPS and Range files is required, for each Flight segment",
                    "items": {
                        "type": "object",
                        "properties": {
                            //NOTE: this uses a negative lookahead to exclude pdf and range files... may need something like this in the imagery rules too
                            "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?(?!.(pdf|\\d{3}|\\d{2}n|\\d{2}g|\\d{1}n|\\d{1}g|n|g))$", "errorMessage": "Flight part name violates the naming standard:" },
                            // "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                            "range": {
                                "type": "array",
                                "minItems": 0,
                                "errorMessage": "A minimum of at least one set of Range files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/range" },
                            },
                            "kmz": {
                                "type": "array",
                                "minItems": 0,
                                "errorMessage": "A minimum of at least one set of LiDAR coverage files (*.kmz) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/kmz" },
                            },
                            // "pos": {
                            //     "type": "array",
                            //     "minItems": 0,
                            //     "errorMessage": "temp rule for edge POS files",
                            //     "items": { "$ref": "#/fileTypes/pos" },
                            // },
                            "gps": {
                                "type": "array",
                                "minItems": 1,
                                "contiguousNumericRange": true,
                                "errorMessage": "A minimum of at least one GPS file is required, there can be no gaps in the GPS file sequence and confirm correct folder naming if using an Eclipse sensor.",
                                "items": { "$ref": "#/fileTypes/gps" },
                            },
                        },
                        "required": ["gps","range"]
                    }
                },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        // "gps": {
                        //     "type": "array",
                        //     "contiguousNumericRange": true,
                        //     "errorMessage": "There are gaps in the GPS files",
                        //     "items": { "$ref": "#/fileTypes/gps" },
                        // },
                        // "gps": {
                        //     "type": "array",
                        //     "minItems": 1,
                        //     "contiguousNumericRange": true,
                        //     "errorMessage": "A minimum of at least one GPS file is required, there can be no gaps in the GPS file sequence and confirm correct folder naming if using an Eclipse sensor.",
                        //     "items": { "$ref": "#/fileTypes/gps" },
                        // },
                        "pdf": {
                            "type": "array",
                            "minItems": 0,
                            "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                            "items": { "$ref": "#/fileTypes/pdf" },
                        },
                        "kmz_flightplan": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one flight plan (*.kmz) is required",
                            "items": { "$ref": "#/fileTypes/kmz_flightplan" }
                        },
                        "log": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one FMS Nav log files (FMS_Flight_Log_*.log) is required for each Flight segment",
                            "items": { "$ref": "#/fileTypes/log" },
                        },
                        // "edgePOS": {
                        //     "type": "array",
                        //     "minItems": 0,
                        //     "errorMessage": "temp rule for edge POS files",
                        //     "items": { "$ref": "#/fileTypes/edgePOS" },
                        // },
                        "africaGps": {
                            "type": "array",
                            "minItems": 0,
                            "errorMessage": "temp rule for edge T04 files",
                            "items": { "$ref": "#/fileTypes/africaGps" },
                        },
                    },
                    "required": ["kmz_flightplan", "log"]
                    // "required": ["gps","kmz_flightplan","log"]
                }
            }
        },
        "OPTECH_GPS": {
            "labels": ["Optech GPS"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "flightParts": {
                    "type": "array",
                    "minItems": 1,
                    "errorMessage": "A minimum of at least one set of Flight GPS is required, for each Flight segment",
                    "items": {
                        "type": "object",
                        "properties": {
                            //NOTE: this uses a negative lookahead to exclude pdf and range files... may need something like this in the imagery rules too
                            "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?(?!.(pdf|\\d{3}|\\d{2}n|\\d{2}g|\\d{1}n|\\d{1}g|n|g))$", "errorMessage": "Flight part name violates the naming standard:" },
                            // "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                            // "kmz": {
                            //     "type": "array",
                            //     "minItems": 0,
                            //     "errorMessage": "A minimum of at least one set of LiDAR coverage files (*.kmz) is required for each Flight segment",
                            //     "items": { "$ref": "#/fileTypes/kmz" },
                            // },
                            // "pos": {
                            //     "type": "array",
                            //     "minItems": 0,
                            //     "errorMessage": "temp rule for edge POS files",
                            //     "items": { "$ref": "#/fileTypes/pos" },
                            // },
                            "gps": {
                                "type": "array",
                                "minItems": 1,
                                "contiguousNumericRange": true,
                                "errorMessage": "A minimum of at least one GPS file is required, there can be no gaps in the GPS file sequence and confirm correct folder naming if using an Eclipse sensor.",
                                "items": { "$ref": "#/fileTypes/gps" },
                            },
                        },
                        "required": ["gps"]
                    }
                },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        // "gps": {
                        //     "type": "array",
                        //     "minItems": 1,
                        //     "contiguousNumericRange": true,
                        //     "errorMessage": "A minimum of at least one GPS file is required, there can be no gaps in the GPS file sequence and confirm correct folder naming if using an Eclipse sensor.",
                        //     "items": { "$ref": "#/fileTypes/gps" },
                        // },
                        // "pdf": {
                        //     "type": "array",
                        //     "minItems": 0,
                        //     "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                        //     "items": { "$ref": "#/fileTypes/pdf" },
                        // },
                        // "kmz_flightplan": {
                        //     "type": "array",
                        //     "minItems": 1,
                        //     "errorMessage": "A minimum of at least one flight plan (*.kmz) is required",
                        //     "items": { "$ref": "#/fileTypes/kmz_flightplan" }
                        // },
                        // "log": {
                        //     "type": "array",
                        //     "minItems": 1,
                        //     "errorMessage": "A minimum of at least one FMS Nav log files (FMS_Flight_Log_*.log) is required for each Flight segment",
                        //     "items": { "$ref": "#/fileTypes/log" },
                        // },
                    },
                    "required": []
                }
            }
        },
        "IMAGERY": {
            "labels": ["Medium Format Imagery"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "flightParts": {
                    "type": "array",
                    "minItems": 1,
                    "errorMessage": "A minimum of at least one set of Flight GPS, EXIF logs and Imagery files is required for each Flight segment",
                    "items": {
                        "type": "object",
                        "properties": {
                            "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                            // "gps": {
                            //     "type": "array",
                            //     "errorMessage": "A minimum of at least one set of GPS files is required for each Flight segment",
                            //     "items": { "$ref": "#/fileTypes/gps" },
                            // },
                            "gps": {
                                "type": "array",
                                "minItems": 1,
                                "contiguousNumericRange": true,
                                "errorMessage": "A minimum of at least one set of GPS files is required for each Flight segment and the file suffixes must be sequential numbers without any gaps",
                                "items": { "$ref": "#/fileTypes/gps" },
                            },
                            "csv": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of EXIF logs (*.csv) files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/csv" },
                            },
                            "xls": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of Excel logs (*.xls) files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/xls" },
                            },
                            // "log": {
                            //     "type": "array",
                            //     "minItems": 1,
                            //     "errorMessage": "A minimum of at least one Log (*.log) files is required for each Flight segment",
                            //     "items": { "$ref": "#/fileTypes/csv" },
                            // },
                            // "jpg": {
                            //     "type": "array",
                            //     "minItems": 0,
                            //     "errorMessage": "A minimum of at least one image coverage overview jpg is required for each Flight segment",
                            //     "items": { "$ref": "#/fileTypes/jpg" },
                            // },
                            // "jgw": {
                            //     "type": "array",
                            //     "minItems": 0,
                            //     "errorMessage": "A minimum of at least one image coverage overview world file is required for each Flight segment",
                            //     "items": { "$ref": "#/fileTypes/jgw" },
                            // },
                            "pdf": {
                                "type": "array",
                                "minItems": 0,
                                "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/pdf" },
                            }
                        },
                        "required": ["gps", "csv"]//, "iiq"]//, "jpg", "jgw", "pdf" ]
                    }
                },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "iiq_no_ir": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one set of Imagery (*.iiq) files is required",
                            "items": { "$ref": "#/fileTypes/iiq_no_ir" },
                        }
                    },
                    //"required": ["iiq"]
                    "required": []//iiq made optional per LS-362
                }
            }
        },
        "IMAGERY_GPS": {
            "labels": ["Medium Format Imagery GPS"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "flightParts": {
                    "type": "array",
                    "minItems": 1,
                    "errorMessage": "A minimum of at least one set of Flight GPS, for each Flight segment",
                    "items": {
                        "type": "object",
                        "properties": {
                            "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                            "gps": {
                                "type": "array",
                                "minItems": 1,
                                "contiguousNumericRange": true,
                                "errorMessage": "A minimum of at least one set of GPS files is required for each Flight segment and the file suffixes must be sequential numbers without any gaps",
                                "items": { "$ref": "#/fileTypes/gps" },
                            },
                            "csv": {
                                "type": "array",
                                "minItems": 0,
                                "errorMessage": "A minimum of at least one set of EXIF logs (*.csv) files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/csv" },
                            },
                            "xls": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of Excel logs (*.xls) files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/xls" },
                            },
                            "pdf": {
                                "type": "array",
                                "minItems": 0,
                                "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/pdf" },
                            }
                        },
                        "required": ["gps", "csv"]
                    }
                }
            }
        },
        "A3_IMAGERY": {
            "labels": ["A3 Edge Imagery"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "flightParts": {
                    "type": "array",
                    "minItems": 1,
                    "errorMessage": "A minimum of at least one set of Flight GPS and Imagery files is required, for each Flight segment",
                    "items": {
                        "type": "object",
                        "properties": {
                            "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                            // "gpso": {
                            //     "type": "array",
                            //     "minItems": 1,
                            //     "errorMessage": "A minimum of at least one set of GNSS / INS files (*.GPSO) is required for each Flight segment",
                            //     "items": { "$ref": "#/fileTypes/gpso" },
                            // },
                            // "gpsn": {
                            //     "type": "array",
                            //     "minItems": 1,
                            //     "errorMessage": "A minimum of at least one set of GNSS / INS files (*.GPSN) is required for each Flight segment",
                            //     "items": { "$ref": "#/fileTypes/gpsn" },
                            // },
                            "pdf": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/pdf" },
                            },
                            "xls": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of Excel logs (*.xls) files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/xls" },
                            }
                        },
                        "required": ["gpso", "gpsn"]
                        // "required": []
                    }
                },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "bin": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one Image file (*.bin) is required",
                            "items": { "$ref": "#/fileTypes/bin" },
                        },
                    },
                    // "required": ["bin"]
                }
            }
        },
        "A3_IMAGERY_GPS": {
            "labels": ["A3 Edge Imagery GPS"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "flightParts": {
                    "type": "array",
                    "minItems": 1,
                    "errorMessage": "A minimum of at least one set of Flight GPS and Imagery files is required, for each Flight segment",
                    "items": {
                        "type": "object",
                        "properties": {
                            "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                            "gpso": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of GNSS / INS files (*.GPSO) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/gpso" },
                            },
                            "gpsn": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of GNSS / INS files (*.GPSN) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/gpsn" },
                            },
                            "pdf": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/pdf" },
                            },
                            "xls": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of Excel logs (*.xls) files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/xls" },
                            }
                        },
                        "required": ["gpso", "gpsn"]
                    }
                }
            }
        },
        "DMC_IMAGERY": {
            "labels": ["DMC 1 Imagery"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "flightParts": {
                    "type": "array",
                    "minItems": 1,
                    "errorMessage": "A minimum of at least one set of Flight GPS and Imagery files is required, for each Flight segment",
                    "items": {
                        "type": "object",
                        "properties": {
                            "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                            // "gps": {
                            //     "type": "array",
                            //     "minItems": 1,
                            //     "errorMessage": "A minimum of at least one set of GPS files is required for each Flight segment",
                            //     "items": { "$ref": "#/fileTypes/gps" },
                            // },
                            // "gps": {
                            //     "type": "array",
                            //     "minItems": 1,
                            //     "contiguousNumericRange": true,
                            //     "errorMessage": "There are gaps in the GPS files",
                            //     "items": { "$ref": "#/fileTypes/gps" },
                            // },
                            "pdf": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/pdf" },
                            },
                            "xls": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of Excel logs (*.xls) files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/xls" },
                            }
                        },
                        // "required": ["gps"]
                        "required": []
                    }
                },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "dat": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one Image file (*.dat) is required",
                            "items": { "$ref": "#/fileTypes/dat" },
                        },
                        "allFilesImageryTarget": {
                            "type": "array",
                            "minItems": 0,
                            "errorMessage": "",
                            "items": { "$ref": "#/fileTypes/allFilesImageryTarget" },
                        },
                    },
                    // "required": ["dat"]
                }
            }
        },
        "DMC_IMAGERY_GPS": {
            "labels": ["DMC 1 Imagery GPS"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "flightParts": {
                    "type": "array",
                    "minItems": 0,
                    "errorMessage": "A minimum of at least one set of Flight GPS and Imagery files is required, for each Flight segment",
                    "items": {
                        "type": "object",
                        "properties": {
                            "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                            // "gps": {
                            //     "type": "array",
                            //     "minItems": 1,
                            //     "contiguousNumericRange": true,
                            //     "errorMessage": "A minimum of at least one set of GPS files is required for each Flight segment and the file suffixes must be sequential numbers without any gaps",
                            //     "items": { "$ref": "#/fileTypes/gps" },
                            // },
                            "pdf": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/pdf" },
                            },
                            "xls": {
                                "type": "array",
                                "minItems": 1,
                                "errorMessage": "A minimum of at least one set of Excel logs (*.xls) files is required for each Flight segment",
                                "items": { "$ref": "#/fileTypes/xls" },
                            }
                        },
                        // "required": ["gps"]
                        "required": []
                    }
                },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "dmcGpsDat": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one Imagery GPS file (*_GnussImu.dat) is required",
                            "items": { "$ref": "#/fileTypes/dmcGpsDat" },
                        }
                    },
                    "required": ["dmcGpsDat"]
                }
            }
        },
        "TerrainMapperLAZ": {
            "labels": ["Terrain Mapper LAZ"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                // "flightParts": {
                //     "type": "array",
                //     "minItems": 1,
                //     "errorMessage": "A minimum of at least one set of Flight LAZ is required, for each Flight segment",
                //     "items": {
                //         "type": "object",
                //         "properties": {
                //             "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                //             "gps": {
                //                 "type": "array",
                //                 "contiguousNumericRange": true,
                //                 "errorMessage": "There are gaps in the GPS files",
                //                 "items": { "$ref": "#/fileTypes/gps" },
                //             },
                //             "pdf": {
                //                 "type": "array",
                //                 "minItems": 1,
                //                 "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                //                 "items": { "$ref": "#/fileTypes/pdf" },
                //             },
                //             "xls": {
                //                 "type": "array",
                //                 "minItems": 1,
                //                 "errorMessage": "A minimum of at least one set of Excel logs (*.xls) files is required for each Flight segment",
                //                 "items": { "$ref": "#/fileTypes/xls" },
                //             }
                //         },
                //         "required": ["gps"]
                //     }
                // },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "laz": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one LAZ file (*.laz) is required",
                            "items": { "$ref": "#/fileTypes/laz" },
                        },
                    },
                    "required": ["laz"]
                }
            }
        },
        "TerrainMapperSOL": {
            "labels": ["Terrain Mapper SOL"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                // "flightParts": {
                //     "type": "array",
                //     "minItems": 1,
                //     "errorMessage": "A minimum of at least one set of Flight LAZ is required, for each Flight segment",
                //     "items": {
                //         "type": "object",
                //         "properties": {
                //             "flightPart": { "type": "string", "pattern": "^FL\\d{6}(_\\d{2})?$", "errorMessage": "Flight part name violates the naming standard:" },
                //             "gps": {
                //                 "type": "array",
                //                 "contiguousNumericRange": true,
                //                 "errorMessage": "There are gaps in the GPS files",
                //                 "items": { "$ref": "#/fileTypes/gps" },
                //             },
                //             "pdf": {
                //                 "type": "array",
                //                 "minItems": 1,
                //                 "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
                //                 "items": { "$ref": "#/fileTypes/pdf" },
                //             },
                //             "xls": {
                //                 "type": "array",
                //                 "minItems": 1,
                //                 "errorMessage": "A minimum of at least one set of Excel logs (*.xls) files is required for each Flight segment",
                //                 "items": { "$ref": "#/fileTypes/xls" },
                //             }
                //         },
                //         "required": ["gps"]
                //     }
                // },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "sol": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one SOL file (*.sol) is required",
                            "items": { "$ref": "#/fileTypes/sol" },
                        },
                    },
                    "required": ["sol"]
                }
            }
        },
        "TerrainMapperGPS": {
            "labels": ["Terrain Mapper GPS"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "tmGpsDat": {
                            "type": "array",
                            "minItems": 1,
                            "errorMessage": "A minimum of at least one terrain mapper GPS file (*.dat) is required",
                            "items": { "$ref": "#/fileTypes/tmGpsDat" },
                        },
                        // "tmGpsRnv": {
                        //     "type": "array",
                        //     "minItems": 1,
                        //     "errorMessage": "A minimum of at least one terrain mapper GPS file (*.rnv) is required",
                        //     "items": { "$ref": "#/fileTypes/tmGpsRnv" },
                        // },
                        // "tmGpsTimeSync": {
                        //     "type": "array",
                        //     "minItems": 1,
                        //     "errorMessage": "A minimum of at least one terrain mapper TimeSync.raw file is required",
                        //     "items": { "$ref": "#/fileTypes/tmGpsTimeSync" },
                        // },
                    },
                    // "required": ["tmGpsDat","tmGpsRnv"]
                    "required": ["tmGpsDat"]
                }
            }
        },
        "TerrainMapperRAW": {
            "labels": ["Terrain Mapper RAW"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        // "tmGpsDat": {
                        //     "type": "array",
                        //     "maxItems": 1,
                        //     "errorMessage": "A maximum of one terrain mapper GPS file (*.dat) will be uploaded",
                        //     "items": { "$ref": "#/fileTypes/tmGpsDat" },
                        // },
                        "allFilesTerrainMapperRAW": {
                            "type": "array",
                            "minItems": 0,
                            "errorMessage": "",
                            "items": { "$ref": "#/fileTypes/allFilesTerrainMapperRAW" },
                        },
                    },
                    "required": []
                }
            }
        },
        "TerrainMapperLIDAR": {
            "labels": ["Terrain Mapper RAW Lidar"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "allFilesTerrainMapperRAW": {
                            "type": "array",
                            "minItems": 0,
                            "errorMessage": "",
                            "items": { "$ref": "#/fileTypes/allFilesTerrainMapperRAW" },
                        },
                    },
                    "required": []
                }
            }
        },
        "TerrainMapperImagery": {
            "labels": ["Terrain Mapper RAW Imagery"],
            "type": "object",
            "properties": {
                "basePath": { "type": "string", "pattern": "^FLIGHT_ID_SENSOR_NUMBER$", "errorMessage": "The base folder name violates the naming standard, or doesn't match the FlightID and Sensor Number:" },
                "anyFileName": {
                    "type": "object",
                    "properties": {
                        "allF`ilesTerrainMapperRAW": {
                            "type": "array",
                            "minItems": 0,
                            "errorMessage": "",
                            "items": { "$ref": "#/fileTypes/allFilesTerrainMapperRAW" },
                        },
                    },
                    "required": []
                }
            }
        }
    };

    //anotehr specifc rule variation... if simultaneous imagery, GPS is optional
    if (isSimultaneousImagery) {
        let index = rulesSchema["IMAGERY"].properties.flightParts.items.required.indexOf("gps");

        if (index > -1) {
            rulesSchema["IMAGERY"].properties.flightParts.items.required.splice(index, 1);
        }
    }

    //FFS more specific rules, see LS-761
    if (RegExp(/ Vexcell /, "g").test(sensorName)) {
        delete rulesSchema["IMAGERY"].properties.flightParts.items.properties["csv"];
    }

    if (RegExp(/P1-\d{4}_0067/, "g").test(sensorName)) {
        // console.log("...rulesSchema", rulesSchema["IMAGERY"].properties);
        delete rulesSchema["IMAGERY"].properties.anyFileName.properties.iiq_no_ir;
        rulesSchema["IMAGERY"].properties.flightParts.items.properties["iiq_w_ir"] = {
            "type": "array",
            "minItems": 1,
            "errorMessage": "A minimum of at least one set of Imagery (*.iiq) files is required",
            "items": { "$ref": "#/fileTypes/iiq_w_ir" },
        }
        // console.log("...rulesSchema", rulesSchema["IMAGERY"].properties);
    }

    //Urghhhh hate doing this, but am told I have to... in the case of a specific sensor swap out the required gps file type/rule
    //ECLIPSE 362 specific rules should be added above.... not a monkey hack as below
    // if (basePath.slice(-3) === "362") {
    //     // console.log('...ruleSchema',rulesSchema);    
    //     //Eclipse 362 LiDAR adjustments 
    //     delete rulesSchema["LiDAR"].properties.flightParts.items.properties["gps"];
    //     delete rulesSchema["LiDAR"].properties.flightParts.items.properties["pdf"];
    //     rulesSchema["LiDAR"].properties.flightParts.items.properties["pos"] = {
    //         "type": "array",
    //         "minItems": 1,
    //         "maxItems": 1,
    //         "errorMessage": "Sensor 362 requires *.pos GPS files",
    //         "items": { "$ref": "#/fileTypes/pos" },
    //     }
    //     rulesSchema["LiDAR"].properties.flightParts.items.required = ["pos", "range"];
    //     rulesSchema["LiDAR"].properties.anyFileName.required = [];
    //     delete rulesSchema["LiDAR"].properties.anyFileName.properties.gps;
    //     // delete rulesSchema["LIDAR"].properties.anyFileName.properties.gps.contiguousNumericRange;
    //     // rulesSchema["LIDAR"].properties.anyFileName = ["pdf"];
    //     // rulesSchema["LIDAR"].properties.anyFileName.properties.gps.items =  { "$ref": "#/fileTypes/pos" };
    //     // rulesSchema["LIDAR"].properties.anyFileName.required = ["pos"];

    //     //Eclipse 362 IMAGERY adjustments
    //     delete rulesSchema["IMAGERY"].properties.flightParts.items.properties["gps"];
    //     delete rulesSchema["IMAGERY"].properties.flightParts.items.properties["iiq"];
    //     delete rulesSchema["IMAGERY"].properties.flightParts.items.properties["pdf"];
    //     rulesSchema["IMAGERY"].properties.flightParts.items.properties["pos"] = {
    //         "type": "array",
    //         "minItems": 1,
    //         "errorMessage": "Sensor 362 requires *.pos GPS files",
    //         "items": { "$ref": "#/fileTypes/pos" },
    //     }
    //     rulesSchema["IMAGERY"].properties.flightParts.items.properties["rawimage"] = {
    //         "type": "array",
    //         "minItems": 1,
    //         "errorMessage": "Sensor 362 requires *.rawimage files",
    //         "items": { "$ref": "#/fileTypes/rawimage" },
    //     }
    //     rulesSchema["IMAGERY"].properties.anyFileName.properties["pdf"] = {
    //         "type": "array",
    //         "minItems": 0,
    //         "errorMessage": "A minimum of at least one Netsuite Flight Log Report (*.pdf) is required for each Flight segment",
    //         "items": { "$ref": "#/fileTypes/pdf" },
    //     }
    //     rulesSchema["IMAGERY"].properties.flightParts.items.required = ["pos", "rawimage"];
    //     // rulesSchema["IMAGERY"].properties.flightParts.items.required = [];
    //     rulesSchema["IMAGERY"].properties.anyFileName.required = [];
    //     delete rulesSchema["IMAGERY"].properties.anyFileName.properties.gps;
    //     delete rulesSchema["IMAGERY"].properties.anyFileName.properties.iiq;
    // }

    if (basePath.slice(-3) === "418") {//African GPS rules 'africaGps'
        rulesSchema["LiDAR"].properties.flightParts.items.required = ["range"];
        rulesSchema["LiDAR"].properties.anyFileName.required = ['africaGps'];

        delete rulesSchema["LiDAR"].properties.flightParts.items.properties["gps"];
    
        // rulesSchema["LiDAR"].properties.anyFileName.items.properties["africaGps"] = {
        //     "type": "array",
        //     "minItems": 1,
        //     "errorMessage": "temp rule for edge T04 files",
        //     "items": { "$ref": "#/fileTypes/africaGps" },
        // }

    }

    Object.keys(rulesSchema.fileTypes).map(item => {
        rulesSchema.fileTypes[item].pattern = rulesSchema.fileTypes[item].pattern.replace('FLIGHT_ID', flightId)
    })

    let sensorNameLastToken = primarySensorName.split('-').slice(-1)[0].trim()
    const isValidSensorNumber = /^\d{2,3}$/.test(sensorNameLastToken);
    let sensorNumber = isValidSensorNumber ? sensorNameLastToken.padStart(3, '0') : '000'

    let schema: { [key: string]: any } = { "fileTypes": rulesSchema["fileTypes"] };
    uploadTypes.forEach(function (item) {
        // Replace placeholders with actual flight ID and sensor number
        rulesSchema[item].properties.basePath.pattern = rulesSchema[item].properties.basePath.pattern.replace('FLIGHT_ID', flightId)
        rulesSchema[item].properties.basePath.pattern = rulesSchema[item].properties.basePath.pattern.replace('SENSOR_NUMBER', sensorNumber)
        schema = deepmerge(schema, rulesSchema[item])
        if (schema.properties.flightParts) {
            schema.properties.flightParts.items.required = (schema.properties.flightParts.items.required as Array<any>).filter((item, index, self) => self.indexOf(item) === index)
        }
        if (schema.properties.anyFileName && schema.properties.anyFileName.required) {
            schema.properties.anyFileName.required = (schema.properties.anyFileName.required as Array<any>).filter((item, index, self) => self.indexOf(item) === index);
        }
    })
    if (schema.labels.length === 1)
        fileRuleSummary["uploadTypeLabel"] = schema.labels.pop();
    else if (schema.labels.length === 2)
        fileRuleSummary["uploadTypeLabel"] = schema.labels.join(' & ');
    else {
        let lastValue = schema.labels.pop()
        fileRuleSummary["uploadTypeLabel"] = schema.labels.join(', ')
        fileRuleSummary["uploadTypeLabel"] += ' & ' + lastValue;
    }

    let blankFileList = []

    let sortedFileList = [...fileList].sort((a, b) => {
        const pathA = (a as any).webkitRelativePath.toLowerCase();
        const pathB = (b as any).webkitRelativePath.toLowerCase();

        if (pathA < pathB) {
            return -1;
        }
        if (pathA > pathB) {
            return 1;
        }
        return 0;
    })
    // console.log(sortedFileList)

    let TerrainMapperRAWhasGPS = false              // only upload one copy of GPS file for TerrainMapperRAW
    for (var i = 0; i < sortedFileList.length; i++) {
        let isFileValid = false;

        let nameToTest: any = (sortedFileList[i] as any).webkitRelativePath.split('/');
        let sizeToTest: number = (sortedFileList[i] as any).size;
        nameToTest.shift();
        nameToTest = nameToTest.join('/');

        schema.properties.flightParts && Object.keys(schema.properties.flightParts.items.properties).some((item) => {
            if (item != "flightPart") {
                // console.log("...1nameToTest, pattern",nameToTest,schema.fileTypes[item].pattern, RegExp(schema.fileTypes[item].pattern, "g").test(nameToTest))
                if (RegExp(schema.fileTypes[item].pattern, "g").test(nameToTest)) {

                    isFileValid = true;
                    let filenameParts: any = nameToTest.split('/').pop();
                    filenameParts = nameToTest.split('.');
                    filenameParts.pop();
                    let flightName = filenameParts.join('.');

                    if (RegExp(schema.properties.flightParts.items.properties.flightPart.pattern, "g").test(flightName) != false) {
                        if (fileRuleSummary["flightParts"][flightName] == null)
                            fileRuleSummary["flightParts"][flightName] = { [item]: [] };
                        else if (fileRuleSummary["flightParts"][flightName][item] == null)
                            fileRuleSummary["flightParts"][flightName][item] = [];

                        fileRuleSummary["flightParts"][flightName][item].push(nameToTest);
                    }
                    return true;//exits the 'some' loop
                } else {
                    return false
                }
            } else {
                return false
            }
        });
        if (schema.properties.anyFileName && schema.properties.anyFileName.properties && isFileValid === false) {//skip if already found
            Object.keys(schema.properties.anyFileName.properties).some((item) => {
                // console.log("...nameToTest, pattern",nameToTest,schema.fileTypes[item].pattern, RegExp(schema.fileTypes[item].pattern, "g").test(nameToTest))
                if (RegExp(schema.fileTypes[item].pattern, "g").test(nameToTest)) {
                    isFileValid = true;

                    if (fileRuleSummary["anyFileName"][item] == null)
                        fileRuleSummary["anyFileName"][item] = [];
                    fileRuleSummary["anyFileName"][item].push(nameToTest);
                    return true;//exits the 'some' loop
                } else {
                    return false
                }
            });
        }
        if (sizeToTest === 0) {
            isFileValid = false             // exclude blank file(0 byte)
            blankFileList.push(nameToTest)
        }

        let relevantPath: any = (sortedFileList[i] as any).webkitRelativePath.split('/');
        relevantPath.shift();
        relevantPath = relevantPath.join('/');

        //some of the logic below can be simplified with more targetted regex's
        if (isFileValid) {
            /*if (uploadTypes.includes('TerrainMapperGPS') && fileRuleSummary["validFiles"].length) {
                fileRuleSummary["invalidFiles"].push(relevantPath);
            } else */if ((uploadTypes.includes('TerrainMapperRAW') || uploadTypes.includes('TerrainMapperLIDAR') || uploadTypes.includes('TerrainMapperImagery'))
                && RegExp(rulesSchema['fileTypes']['tmGpsDat']['pattern'], "g").test(relevantPath)) {      // take precedence over other rules as there are other generic *.dat files
                if (TerrainMapperRAWhasGPS == false) {
                    fileRuleSummary["validFiles"].push(relevantPath);
                    TerrainMapperRAWhasGPS = true
                    console.log('Found TM GPS file: ', relevantPath)
                } else {
                    fileRuleSummary["invalidFiles"].push(relevantPath);
                }
            } else if (uploadTypes.includes('TerrainMapperLIDAR')) {
                // The validation rules for TerrainMapperLIDAR are all exclusive rules. 
                if (RegExp(rulesSchema['fileTypes']['tmRaw']['pattern'], "g").test(relevantPath)
                    || RegExp(rulesSchema['fileTypes']['tmRawSha1']['pattern'], "g").test(relevantPath)) {
                    // 'TimeSync.raw' and 'TimeSync.raw.sha1' are exceptions
                    if (relevantPath.includes('TimeSync')) {
                        fileRuleSummary["validFiles"].push(relevantPath)
                    } else {
                        fileRuleSummary["invalidFiles"].push(relevantPath);
                    }
                } else if (RegExp(rulesSchema['fileTypes']['tmThumbnails']['pattern'], "g").test(relevantPath)
                    || RegExp(rulesSchema['fileTypes']['tmDarkFrames']['pattern'], "g").test(relevantPath)) {
                    fileRuleSummary["invalidFiles"].push(relevantPath);
                } else {
                    fileRuleSummary["validFiles"].push(relevantPath);
                }
            } else if (uploadTypes.includes('TerrainMapperImagery')) {
                if (RegExp(rulesSchema['fileTypes']['tmDat']['pattern'], "g").test(relevantPath)
                    || RegExp(rulesSchema['fileTypes']['tmDatSha1']['pattern'], "g").test(relevantPath)) {
                    if (RegExp('MM\\d{1,2}\/PRJ\\d{5}').test(relevantPath)   // Upload all imagery files in MM\d{1,2} subfolder except the *_Returns.dat and its sha1 files
                        && !RegExp(rulesSchema['fileTypes']['tmImageryReturnsDat']['pattern'], "gi").test(relevantPath)
                        && !RegExp(rulesSchema['fileTypes']['tmImageryReturnsDatSha1']['pattern'], "gi").test(relevantPath)) {
                        fileRuleSummary["validFiles"].push(relevantPath);
                    } else {
                        fileRuleSummary["invalidFiles"].push(relevantPath);
                    }
                } else {
                    fileRuleSummary["validFiles"].push(relevantPath);
                }
            }
            else {
                fileRuleSummary["validFiles"].push(relevantPath);
            }
        }
        else {
            fileRuleSummary["invalidFiles"].push(relevantPath);
        }
    }

    if (blankFileList.length) {
        fileRuleSummary['alertMessages'].push({ name: 'blankFiles', message: blankFileList })
    }

    fileRuleSummary.flightParts = Object.keys(fileRuleSummary.flightParts).map(key => (
        {
            flightPart: key,
            ...fileRuleSummary.flightParts[key]
        }));

    function hex32(val: number) {
        val &= 0xFFFFFFFF;
        var hex = val.toString(16).toUpperCase();
        return ("00000000" + hex).slice(-8);
    }

    /* The labels used below, 7AA59001, 7AA5900F and 7AA59005 were all given to us by Optech */
    let sensorNo = basePath.slice(-3);//optech sensor specific
    console.log('... Sensor No:', sensorNo, ', testing Range and GPS files match...');
    let matchingSensor = sensorEquipment.filter(item => item.Name.slice(-3) === sensorNo)[0];//really need to have a specifc field in sensor equipment for sensor no, not to be confused with serial no.

    if (fileList instanceof FileList) {
        fileList = [...fileList];//changing to array, as array.filter used below in the range and gps validation
    }

    // Africa specific rules... this checks T04 files for:
    // 1. size.... is it a rover or base file, smal is ttypicaly a base file... these need to be fltered out
    // 2. do the dates in the T04 file names overlap with the wheels up / down
    if (sensorNo === "418") { // hardcoded SA sensor

        // console.log("...default gps: ",rulesSchema["LiDAR"].properties.flightParts.items.properties["gps"]);
        // delete rulesSchema["LiDAR"].properties.flightParts.items.properties["gps"]
        // rulesSchema["LiDAR"].properties.flightParts.items.required = ["africaGps", "range"];

        // console.log("...sortedFileList", sortedFileList);
        const contiguousThresholdSecs: number = 60;//not sure if 60 seconds is too small a gap?
        let groupCounter: number = 0;
        let lastMatch: number = 0;
        let groups: File[][] = [];


        const dateRegex: RegExp = /____(\d{12})\.T04/;
        //grouping gps files into groups, within the threshold seconds
        for (var i = 0; i < sortedFileList.length; i++) {

            const match: RegExpMatchArray | null = sortedFileList[i].name.match(dateRegex);
            if (match) {

                let matchNumber: number = Number(match[1]);
                if (lastMatch == 0) {
                    groups[groupCounter] = [sortedFileList[i]];
                    lastMatch = matchNumber;
                }
                else if (matchNumber - lastMatch <= contiguousThresholdSecs) {
                    let tmpArray = groups[groupCounter];
                    tmpArray.push(sortedFileList[i]);
                    groups[groupCounter] = tmpArray;
                    lastMatch = matchNumber;
                }
                else if (matchNumber - lastMatch > contiguousThresholdSecs) {
                    groupCounter += 1;
                    groups[groupCounter] = [sortedFileList[i]];
                    lastMatch = matchNumber;
                }
            }
        }

        if (groups.length > 1)
            console.log(`... Multiple (${groups.length}) gps date range groups detected!`);

        let wheelsUpDate = new Date(props.wheelsUp.endsWith('.000Z') ? props.wheelsUp : props.wheelsUp + '.000Z')       // Append '.000Z' to indicate UTC time
        let wheelsDownDate = new Date(props.wheelsDown.endsWith('.000Z') ? props.wheelsDown : props.wheelsDown + '.000Z')       // Append '.000Z' to indicate UTC time
        let validT04FilesCounter: number = 0;
        // check for overlap in the wheelsup / wheels down and the different detected gps file groups, concatenate if tehre is overlap... a larger solution is always best
        for (var i = 0; i < groups.length; i++) {
            let matchStart: RegExpMatchArray | null = groups[i][0].name.match(dateRegex);
            let matchEnd: RegExpMatchArray | null = groups[i][groups[i].length - 1].name.match(dateRegex);
            if (matchStart && matchEnd) {
                let startDateTime: Date = parseT04DateString(matchStart[1]);
                let endDateTime: Date = parseT04DateString(matchEnd[1]);

                if (!(wheelsUpDate <= endDateTime && wheelsDownDate >= startDateTime)) { //excluding non-overlapping times
                    for (let j = 0; j < groups[i].length; j++) {
                        let relevantPath: any = (groups[i][j] as any).webkitRelativePath.split('/');
                        relevantPath.shift();
                        relevantPath = relevantPath.join('/');
                        fileRuleSummary["invalidFiles"].push(relevantPath);
                        // console.log("non-overlapping: ",relevantPath);
                    }
                }
                else {
                    console.log(`... Group ${i} overlaps wheelsup/down times, including`)
                    for (let j = 0; j < groups[i].length; j++) {
                        let relevantPath: any = (groups[i][j] as any).webkitRelativePath.split('/');
                        relevantPath.shift();
                        relevantPath = relevantPath.join('/');
                        let sizeToTest: number = (groups[i][j] as any).size;
                        if (sizeToTest < 100000) { //excluding < 100KB
                            console.log(`... Excluding ${relevantPath} due to small file size.`)
                            fileRuleSummary["invalidFiles"].push(relevantPath);
                        }
                    }
                }

                // console.log("1...fileRuleSummary valid: ",fileRuleSummary["validFiles"])
                let newValidFiles = [];
                for (let j = 0; j < fileRuleSummary["validFiles"].length; j++) {
                    if (!fileRuleSummary["invalidFiles"].includes(fileRuleSummary["validFiles"][j])) {
                        newValidFiles.push(fileRuleSummary["validFiles"][j]);
                    }
                }
                fileRuleSummary["validFiles"] = newValidFiles;
                // console.log("2...fileRuleSummary valid: ",fileRuleSummary["validFiles"])
            }
        }

        for (let j = 0; j < fileRuleSummary["validFiles"].length; j++) {
            if (fileRuleSummary["validFiles"][j].match(dateRegex)) {
                validT04FilesCounter += 1;
            }
        }

        if (validT04FilesCounter == 0) {
            fileRuleSummary.errorMessages.push("No valid T04 GNSS files found, they are either missing or may have been excluded due to not overlapping the flight capture timeframe or due to insufficient file size.");
        }

        console.log("...validT04FilesCounter: ",validT04FilesCounter)
    }

    //below only applicable to LiDAR, when checking flightplan kml validity
    if (uploadTypes[0] === "LiDAR") {

        let planData = fileRuleSummary.validFiles.filter(item => RegExp(/PRJ.+\.(KMZ|kmz)/, "g").test(item));

        // let oneFound = false;
        // let incorrectProjects = []

        for (let i = 0; i < planData.length; i++) {
            hasFlightPlan = true;

            console.log('... Scanning flight plan file ' + planData[i] + ' for validity...')
            let _file = fileList.filter(item => item.name === planData[i])[0]

            var new_zip = new JSZip();
            new_zip.loadAsync(_file)
                .then(function (zip) {
                    zip.file("doc.kml").async("string").then(function (result) {
                        let parser = new DOMParser();
                        let xmlDoc = parser.parseFromString(result, "text/xml");
                        if (xmlDoc.getElementsByTagName('parsererror').length > 0)
                            fileRuleSummary.errorMessages.push("Possible corrupt Flight Plan KML, file: '" + _file.name + "', failed validation.");
                        if (result.match(/<B>Line<\/B> = CS/g) != null /*&& result.match(/<B>Line<\/B> = CS/g).length > 0*/) {
                            hasMinOneCS = true;
                        }
                    })
                });
        }
    }

    //below only applicable to LiDAR, when checking range files for serial number
    if (uploadTypes[0] === "LiDAR") {
        let rangeData = fileRuleSummary.validFiles.filter(item => RegExp(/FL\d{6}(_\d{2})?\.range/, "g").test(item));
        for (let i = 0; i < rangeData.length; i++) {
            console.log('... Scanning range file ' + rangeData[i] + ' for serial, gps week and gps time...')
            let _file = fileList.filter(item => item.name === rangeData[i])[0]
            let total64kBlocks = _file.size / 65536;
            let gpsFound, serialFound = false;
            let serialString = undefined;
            for (let i = 0; i < total64kBlocks; i++) {
                let blob64k = _file.slice(i * 65536, (i + 1) * 65536);
                let arrayBuffer64k = await readFileAsyncAsArrayBuffer(blob64k);
                let dv = new DataView(arrayBuffer64k as ArrayBufferLike);
                let label = hex32(dv.getUint32(8))
                if (label === '7AA59001') {
                    let sizeLabel = dv.getInt32(12);
                    for (let j = sizeLabel + 16; j < 65536; j += 4) {
                        let nextLabel = hex32(dv.getInt32(j));
                        if (nextLabel === '7AA5900F') {
                            let GPSTime = dv.getFloat64(j + 16);
                            let GPSWeek = dv.getUint32(j + 28);
                            console.log('... GPS label found, GPSTime:', GPSTime, 'GPSWeek:', GPSWeek)
                            gpsFound = true;
                        }
                        if (nextLabel === '7AA59005') {
                            //serial is 12 bytes after this
                            let decoder = new TextDecoder('utf-8');
                            let bytes = arrayBuffer64k && arrayBuffer64k.slice(j + 16, j + 20)
                            serialString = bytes && decoder.decode(bytes as BufferSource);
                            bytes = arrayBuffer64k && arrayBuffer64k.slice(j + 20, j + 24);
                            let testForNull = new Int8Array(bytes as any);
                            let endIndex = testForNull.length;
                            for (let j = testForNull.length - 1; j >= 0; j--) {
                                if (testForNull[j] === 0) {//the serial will be terminated by 00
                                    endIndex = j;
                                    break;
                                }
                            }
                            let endPart = decoder.decode(bytes as any);
                            endPart = endPart.slice(0, endIndex);
                            serialString += endPart;
                            console.log('... Serial label found:', serialString);
                            serialFound = true;
                        }
                        if (gpsFound && serialFound)
                            break;
                    }
                    if (gpsFound && serialFound)
                        break;
                }
            }
            if (!serialFound) {
                fileRuleSummary.errorMessages.push("Possible corrupt Range file '" + _file.name + "', no laser serial no. could be read");
            }
            else if (matchingSensor === undefined) {
                fileRuleSummary.errorMessages.push("No sensor found with sensor no. '" + sensorNo + "'");
            }
            else if (sensorNo != sensorName.slice(-3)) {
                fileRuleSummary.errorMessages.push("Folder sensor no. '" + sensorNo + "' does not match the expected sensor '" + sensorName + "'");
            }
            else if (serialFound && serialString !== matchingSensor.LaserSerialNo) {
                fileRuleSummary.errorMessages.push("The laser serial no. '" + serialString + "' in Range file '" + _file.name + "', does not match the expected laser serial no. '" + matchingSensor.LaserSerialNo + "'");
            }
        }

        //if has a gps file (*.000...n || *.pos), check that the serial number (read directly from the file) matches the expected sensor serial number
        let gpsData = fileRuleSummary.validFiles.filter(item => RegExp(/FL\d{6}.(\d{3}|pos)/, "g").test(item));
        if (gpsData.length > 0) {
            //should always be the first file
            let _file = fileList.filter(item => item.name === gpsData[0])[0]
            const CHUNK_SIZE = 20000000;//20MB
            let exactParts = Math.ceil(_file.size / CHUNK_SIZE);
            let lastPartSize = _file.size - (Math.floor(_file.size / CHUNK_SIZE) * CHUNK_SIZE);
            let readSerialNo = undefined;
            for (let j = 0; j < exactParts; j++) {
                let start = j * CHUNK_SIZE;
                let end = j === exactParts - 1 ? lastPartSize : start + CHUNK_SIZE;
                let blob = _file.slice(start, end);
                let fileChunk = await readFileAsync(blob as File);
                let serialIndex = (fileChunk as any).indexOf("S/N");
                if (serialIndex !== -1) {
                    readSerialNo = (fileChunk as any).slice(serialIndex, serialIndex + 7);
                    readSerialNo = readSerialNo.slice(3, readSerialNo.length);
                    if (matchingSensor == undefined) {
                        fileRuleSummary.errorMessages.push("No sensor found with sensor no. '" + sensorNo + "'");
                    }
                    else if (readSerialNo !== matchingSensor.POSSerialNo)
                        fileRuleSummary.errorMessages.push("The sensor serial no. '" + readSerialNo + "' in GPS file '" + _file.name + "', does not match the expected sensor serial no. '" + matchingSensor.POSSerialNo + "'");
                    break;
                }
            }
            if (readSerialNo === undefined) {
                fileRuleSummary.errorMessages.push("Possible corrupt GPS file'" + _file.name + "', no sensor serial no. could be read");
            }
        }

        console.log(`... Scanning FMS log files...`)
        let fmsLogFilenameList = fileRuleSummary.validFiles.filter(item => RegExp(/^FMS_Flight_Log_([\d\w_-]+).(log|LOG)$/, 'g').test(item))

        const CHUNK_SIZE = 8 * 1024 * 1024         // 8MB
        const projectIdRegexp = /(PRJ|prj)\d{5}/g
        var fmsLogFilenameListWithNoProjectId: Array<string> = []
        var fmsLogFilenameListWithUnexpectedProjectId: Array<string> = []       // more project ids than database
        var uniqueMatchedProjectIdListGlobal: Array<string> = []
        var missingProjectIdList: Array<string> = []
        var globalGpsTimeStartUtcTimestamp: number = 0
        var globalGpsTimeEndUtcTimestamp: number = 0


        for (const filename of fmsLogFilenameList) {
            let logFile = fileList.filter(item => item.name === filename)[0]
            console.log(`    Parsing FMS log: ${filename}`)

            let exactParts = Math.ceil(logFile.size / CHUNK_SIZE)
            let lastPartSize = logFile.size - (Math.floor(logFile.size / CHUNK_SIZE) * CHUNK_SIZE)
            let uniqueMatchedProjectIdList: Array<string> = []
            var firstMissionLine: string = ''
            var firstTraceLine: string = ''         // first $TRACE line with gps offset: first occurance of '$TRACE' !== 0.00
            var lastTraceLine: string = ''         // first $TRACE line with gps offset: first occurance of '$TRACE' !== 0.00

            for (let i = 0; i < exactParts; i++) {
                let start = i * CHUNK_SIZE;
                let end = (i === exactParts - 1) ? (start + lastPartSize) : (start + CHUNK_SIZE);
                let blob = logFile.slice(start, end);
                let fileChunk = await readFileAsync(blob as File);

                let lines = (fileChunk as string).split(/\r?\n/)
                if (firstMissionLine.length == 0) {
                    for (let j = 0; j < lines.length; j++) {
                        if (RegExp(/\$MISSION/, "g").test(lines[j])) {
                            firstMissionLine = lines[j]
                            break
                        }
                    }
                }
                if (firstTraceLine.length == 0) {
                    for (let j = 0; j < lines.length; j++) {
                        if (RegExp(/\$TRACE/, "g").test(lines[j])) {
                            let traceLineParts = lines[j].split(',')
                            if (traceLineParts[1] != '0.00') {
                                firstTraceLine = lines[j]
                                break
                            }
                        }
                    }
                }
                for (let j = lines.length; j >= 0; j--) {
                    if (RegExp(/\$TRACE/, "g").test(lines[j])) {
                        let traceLineParts = lines[j].split(',')
                        if (traceLineParts[1] != '0.00') {
                            lastTraceLine = lines[j]
                            break
                        }
                    }
                }

                let matchedProjectIdList = (fileChunk as string).match(projectIdRegexp) || []
                let uniqueMatchedProjectIdListInChunk = matchedProjectIdList.filter((projectId, index, array) => array.indexOf(projectId) === index)
                uniqueMatchedProjectIdListInChunk.forEach(projectId => {
                    if (!uniqueMatchedProjectIdList.includes(projectId)) {
                        uniqueMatchedProjectIdList.push(projectId)
                    }
                })
            }
            if (uniqueMatchedProjectIdList.length === 0) {
                fmsLogFilenameListWithNoProjectId.push(logFile.name)
            }
            if (uniqueMatchedProjectIdList.length && !checkSubsetArrContain(projectIdList, uniqueMatchedProjectIdList)) {
                fmsLogFilenameListWithUnexpectedProjectId.push(logFile.name)
            }
            uniqueMatchedProjectIdList.forEach(projectId => {
                if (!uniqueMatchedProjectIdListGlobal.includes(projectId)) {
                    uniqueMatchedProjectIdListGlobal.push(projectId)
                }
            })

            let traceGpsStartDatetimeStr = firstTraceLine.split(',')[1]
            let traceGpsEndDatetimeStr = lastTraceLine.split(',')[1]
            let missionStartDateStr = firstMissionLine.split(',')[2]
            let missionEndDateStr = missionStartDateStr
            if (Number(traceGpsStartDatetimeStr) > Number(traceGpsEndDatetimeStr)) {        // for the scenario that a flight departs on Saturday and arrives on Sunday morning
                let missionEndDate = new Date(missionEndDateStr);
                missionEndDate.setTime(missionEndDate.getTime() + (24 * 60 * 60 * 1000));
                missionEndDateStr = missionEndDate.toISOString().split('T')[0];
            }

            let gpsStartUtcTime = convertFmsGpsTimeToUtc(missionStartDateStr, traceGpsStartDatetimeStr, 18)        // the leaf second is 18s since 2016
            let gpsEndUtcTime = convertFmsGpsTimeToUtc(missionEndDateStr, traceGpsEndDatetimeStr, 18)
            globalGpsTimeStartUtcTimestamp = globalGpsTimeStartUtcTimestamp
                ? Math.min(globalGpsTimeStartUtcTimestamp, gpsStartUtcTime.getTime())
                : gpsStartUtcTime.getTime()
            globalGpsTimeEndUtcTimestamp = Math.max(globalGpsTimeEndUtcTimestamp, gpsEndUtcTime.getTime())

        }
        if (uniqueMatchedProjectIdListGlobal.length === 0 || !checkSubsetArrContain(uniqueMatchedProjectIdListGlobal, projectIdList)) {
            projectIdList.forEach(projectId => {
                if (!uniqueMatchedProjectIdListGlobal.includes(projectId)) {
                    missingProjectIdList.push(projectId)
                }
            })
        }
        if (fmsLogFilenameListWithNoProjectId.length
            || fmsLogFilenameListWithUnexpectedProjectId.length
            || missingProjectIdList.length) {
            fileRuleSummary['alertMessages'].push({ name: 'FMSLogMismatchProject', message: { fmsLogFilenameListWithNoProjectId, fmsLogFilenameListWithUnexpectedProjectId, missingProjectIdList } })
        }

        let wheelsUpDate = new Date(props.wheelsUp.endsWith('.000Z') ? props.wheelsUp : props.wheelsUp + '.000Z')       // Append '.000Z' to indicate UTC time
        let wheelsDownDate = new Date(props.wheelsDown.endsWith('.000Z') ? props.wheelsDown : props.wheelsDown + '.000Z')       // Append '.000Z' to indicate UTC time

        let flightStartTimeDiff = Math.abs(wheelsUpDate.getTime() - globalGpsTimeStartUtcTimestamp)
        let flightEndTimeDiff = Math.abs(wheelsDownDate.getTime() - globalGpsTimeEndUtcTimestamp)
        if (flightStartTimeDiff >= FMS_GPS_TIME_MARGIN_IN_MS || flightEndTimeDiff >= FMS_GPS_TIME_MARGIN_IN_MS) {
            // console.log(`timeDiff is greater than MARGIN: ${flightStartTimeDiff}, ${flightEndTimeDiff}`)
            fileRuleSummary['alertMessages'].push({ name: 'FMSLogMismatchTime', message: { fmsLogFilenameList } })
        } else {
            // console.log(`timeDiff is less than MARGIN: ${flightStartTimeDiff}, ${flightEndTimeDiff}`)
        }
    }

    //below only applicable to TerrainMapperLAZ
    if (uploadTypes[0] === "TerrainMapperLAZ") {
        console.log(`... Scanning LAS/LAZ files...`)
        let lazFileNameList = fileRuleSummary.validFiles.filter(item => RegExp(/.(laz|LAZ)$/, 'g').test(item))

        const CHUNK_SIZE = 0.3 * 1024 * 1024         // 300kB is enough for reading header
        let isRGBNProcessingTemplate = processingTemplate.toUpperCase().split('-').slice(-1)[0].includes('RGBN')
        let filenameListWithoutRGBN = []
        let invalidLazFilenameList = []

        for (const filename of lazFileNameList) {
            let lazFile = fileList.filter(item => item.name === filename)[0]
            // console.log(`    Parsing LAS/LAZ file: ${filename}`)
            let relevantPath: any = (lazFile as any).webkitRelativePath.split('/');
            relevantPath.shift();
            relevantPath = relevantPath.join('/');
            // console.log(relevantPath)

            let blob = lazFile.slice(0, CHUNK_SIZE)
            try {
                const header = await lasHeader.readFileObject({ input: blob });
                // console.log(header)

                if (isRGBNProcessingTemplate) {
                    let formatId = header['PointDataFormatID'] & 0x3f;
                    // console.log(`Format ID: ${formatId}`)
                    if (formatId !== 8) {
                        filenameListWithoutRGBN.push(relevantPath)
                    }
                }
            } catch (e) {
                invalidLazFilenameList.push(relevantPath)
            }

        }
        if (invalidLazFilenameList.length) {
            fileRuleSummary['alertMessages'].push({ name: 'lazHeader', message: { invalidLazFilenameList } })
        }
        if (filenameListWithoutRGBN.length) {
            fileRuleSummary['alertMessages'].push({ name: 'lazRGBN', message: { filenameListWithoutRGBN } })
        }
    }

    // console.log(schema)
    // console.log(fileRuleSummary)
    console.log('... Validating Folder Rules');
    var valid = ajv.validate(schema, fileRuleSummary);
    if (!valid && !isPreexistingUpload) {/// previous uploads aren't evaluated as a whole
        console.log('... Folder validation failed');
        ajv.errors && ajv.errors.forEach(function (error) {
            let errorMessage = "";
            if (error.keyword == "required") {
                errorMessage = error.schema[(error.params as any).missingProperty].errorMessage;
            }
            else if (error.data instanceof Array) {
                error.data = error.data.join(', ');//convert to a string that can be wrapped
                errorMessage = (error.parentSchema as any).errorMessage + ' ' + error.data;
            }
            else {
                errorMessage = (error.parentSchema as any).errorMessage + ' ' + error.data;
            }
            fileRuleSummary["errorMessages"].push(errorMessage);
        });
    }

    console.log('... Folder validation passed, commencing compression and upload');
    // console.log(fileRuleSummary)
    return ({
        fileRuleSummary,
        isMissingFlightplanCS: hasFlightPlan && !hasMinOneCS ? true : false,
    })
}

export default folderValidationHelper